<template>
  <div id="main" class="page-root">
    <div class="flex h-full min-h-0" :class="mobile ? 'flex-col' : 'flex-row'">
      <!-- Icon rail per design Sidebar.dc.html: labeled icon pills, active =
           solid plum; quiet utilities + the connection status circle at the
           bottom. (Obuka/training is kept although the mockup omits it —
           recorded as a divergence in the spec's P2 record.) -->
      <div v-if="!mobile" class="sidebar-menu shrink-0">
        <div class="sidebar-top">
          <div v-if="brand" class="sidebar-brand">
            <img :src="brand.logo.darkShort" :alt="brand.displayName" />
          </div>
          <div
            v-for="(page, index) in appPages"
            :key="'page' + index"
            class="rail-item"
            :class="{ selected: page.active, disabled: page.disabled, attention: page.iconWarning && !page.active }"
            @click="goToPage(page)"
          >
            <!-- Attention state (orders waiting in the hub) renders the SOLID
                 icon in the warning color; otherwise the rail stays outline. -->
            <UIcon :name="page.iconWarning && !page.active ? (page.iconSolid || page.icon) : page.icon" class="rail-icon" />
            <span class="rail-label">{{ page.title }}</span>
            <span v-if="page.badge" class="rail-badge">{{ page.badge }}</span>
          </div>
        </div>

        <div class="additional">
          <div class="rail-divider"></div>
          <div class="rail-item quiet" @click="refreshApp">
            <UIcon name="i-lucide-rotate-cw" class="rail-icon rail-icon--sm" />
            <span class="rail-label">{{ t('general.refresh') }}</span>
          </div>
          <div
            v-if="I18n.c('general.allowTrainingMode')"
            class="rail-item quiet"
            :class="{ training: store.trainingMode }"
            @click="toggleTrainingMode"
          >
            <UIcon name="i-lucide-presentation" class="rail-icon rail-icon--sm" />
            <span class="rail-label">{{ t('general.training') }}</span>
          </div>

          <div class="rail-item quiet" @click="logout">
            <UIcon name="i-lucide-log-out" class="rail-icon rail-icon--sm" />
            <span class="rail-label">{{ t('general.log_out') }}</span>
          </div>

          <connection-management-button class="rail-status" />
        </div>
      </div>

      <!-- This column owns the Android edge-to-edge status-bar inset at every
           width: the routed page headers carry none of their own, and the rail
           beside it on wide screens pads only itself. Empty on desktop. -->
      <div class="flex min-h-0 flex-1 flex-col pt-[var(--app-safe-area-top,env(safe-area-inset-top,0px))]">
        <!-- Training-mode banner: the ONE global marker for the mode. Lives in
             the shell so it spans every routed page (the mode is device-global,
             not a sales-screen state) on both breakpoints; solid error surface
             with white content, ended in place via the outline action. -->
        <div v-if="store.trainingMode" class="training-banner">
          <UIcon name="i-lucide-presentation" class="size-4.5 shrink-0" />
          <span class="text-section-label min-w-0 leading-snug">{{ t('general.training_banner') }}</span>
          <UButton
            size="sm"
            variant="outline"
            color="neutral"
            class="ml-auto shrink-0 text-white ring-white/50 hover:bg-white/10"
            @click="toggleTrainingMode"
          >
            {{ t('general.training_stop') }}
          </UButton>
        </div>

        <alert-banners />

        <div class="relative min-h-0 flex-1">
          <UProgress
            v-if="loading"
            size="sm"
            class="absolute inset-x-0 top-0 z-10"
          />

          <!-- Sales tree keeps its state across tab switches: Sales
               (retail Order / hospitality Tables inside) and the hospitality
               order screen. Everything else remounts per entry. -->
          <!-- Desktop rail navigation cross-fades pages (motion tokens); mobile
               tab switches stay instant (no transition name → no classes). -->
          <router-view v-slot="{ Component }">
            <transition :name="mobile ? 'none' : 'page-switch'" mode="out-in">
              <keep-alive :include="['Sales', 'SalesOrder']">
                <component :is="Component" />
              </keep-alive>
            </transition>
          </router-view>
        </div>
      </div>

      <nav
        v-if="mobile"
        data-bottom-bar
        class="flex shrink-0 bg-muted pb-[var(--app-safe-area-bottom,env(safe-area-inset-bottom))]"
      >
        <button
          v-for="(page, index) in appPages"
          :key="'tab' + index"
          type="button"
          class="tab-btn relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5"
          :class="[
            page.active ? 'text-(--app-text-accent)' : (page.iconWarning ? 'text-warning' : 'text-muted'),
            {
              'tab-btn--solid': page.active || page.iconWarning,
              'pointer-events-none opacity-50': page.disabled
            }
          ]"
          :disabled="page.disabled"
          @click="goToPage(page)"
        >
          <!-- Both glyphs stay mounted so a switch can crossfade them. -->
          <span class="tab-icon relative size-6">
            <UIcon :name="page.iconLine || page.icon" class="tab-glyph tab-glyph--line" />
            <UIcon :name="page.iconSolid || page.icon" class="tab-glyph tab-glyph--solid" />
          </span>
          <span class="tab-label text-helper">{{ page.title }}</span>
          <span
            v-if="page.badge"
            class="absolute right-1/2 top-0.5 -mr-5 flex size-[18px] items-center justify-center rounded-full bg-error text-chip-label leading-none text-white"
          >{{ page.badge }}</span>
        </button>
      </nav>
    </div>

    <!-- Singleton overlays mount lazily: portals stack by DOM order (nothing
         carries a z-index), so a persistent teleport anchor from layout mount
         would paint BELOW any sheet opened later. The key forces a FRESH
         instance on every open — the teleport anchor is created at mount, so
         reusing a still-mounted instance (the unmount grace below) would
         reopen at a stale body position, under overlays opened since. Unmount
         is delayed so the falling-edge cancel watchers still settle. -->
    <client-select-modal
      v-if="clientSelect.mounted.value"
      :key="'cs' + clientSelect.instanceKey.value"
      v-model="isClientSelectModalOpen"
      :config="selectClientConfig"
    />

    <numpad-modal
      v-if="numpad.mounted.value"
      :key="'np' + numpad.instanceKey.value"
      v-model="isNumpadOpen"
      :config="numpadConfig"
    />

    <terminal-register
      v-if="terminalRegister.mounted.value"
      :key="'tr' + terminalRegister.instanceKey.value"
      v-model="showTerminalRegister"
    />

    <cod-confirm
      v-if="codConfirmMount.mounted.value"
      :key="'cc' + codConfirmMount.instanceKey.value"
      v-model="isCodConfirmOpen"
      :config="codConfirmConfig"
    />

    <table-session-pin-modal
      v-if="qrEnabled"
      v-model="showPinModal"
      :session="pinModalSession"
    />
  </div>
</template>

<script setup lang="ts">
import { ComputedRef, Ref, computed, defineAsyncComponent, inject, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { INumpadConfig, ISelectClientConfig } from '@/types/components';
import { CodConfirm, ICodConfirmConfig } from 'app-components';
import { useAppStore } from '@/stores/useAppStore';
import { connectionService, I18n, Store, UserService } from 'app-core';
import { useDisplay } from '@/composables/useDisplay';
import { useApp } from '@/composables/useApp';
import NumpadModal from '@/components/NumpadModal.vue';
import ClientSelectModal from '@/components/ClientSelectModal.vue';
import { Locale } from 'app-core';
import ConnectionManagementButton from '@/components/ConnectionManagementButton.vue';
import TerminalRegister from '@/components/TerminalRegister.vue';
import AlertBanners from '@/components/AlertBanners.vue';
const TableSessionPinModal = defineAsyncComponent(() => import('@/components/TableSessionPinModal.vue'))
import { useOrdersHub } from '@/composables/useOrdersHub'
import { useSettlementReminder } from '@/composables/useWorkingDayClose'
import { useOrdersHubIngestion } from '@/composables/useOrdersHubIngestion'
import { useAppEventBackupPoll } from '@/composables/useAppEventBackupPoll'
import { registerAlertHost } from '@/composables/useAlert';
import { useToast } from '@/composables/useToast';
import { Emitter } from '@/utilities/emitter';
import { TableSession, TableSessionService } from 'app-core';

interface IAppPage {
  title: string
  url: string
  icon: string
  /** Mobile bottom bar glyph pair (Phosphor): the line weight while the tab
   *  is not selected, its -fill twin when selected (or warning). The desktop
   *  rail stays on the Lucide outline set (design §2). */
  iconLine?: string
  iconSolid?: string
  disabled: boolean
  active: boolean
  badge?: number | null
  iconWarning?: boolean
}

const router = useRouter()
const route = useRoute()
const store = useAppStore()
const brand = computed(() => Locale.getBrand())
const { t } = useI18n()
const { mobile } = useDisplay()
const { loading } = useApp()
const { toast } = useToast()
const { isVisible: ordersHubVisible, badgeCount: ordersHubBadgeCount, hasActiveOrders: ordersHubHasActive } = useOrdersHub()
const { settlementOverdue } = useSettlementReminder()

// Authenticated-session Orders Hub intake: unified 60s poller, real-time push
// listeners, auto-fire-to-kitchen, and audio/toast alerts. Anchored to this
// layout's (post-login) lifecycle — see useOrdersHubIngestion.
useOrdersHubIngestion()

// Reliability backup poll: 60s backstop that replays missed AppEvents through
// AppEvent.handle() (multi-terminal venues only). See useAppEventBackupPoll.
useAppEventBackupPoll()

const isNumpadOpen = ref(false)
const isClientSelectModalOpen = ref(false)
const isCodConfirmOpen = ref(false)
const numpadConfig: Ref<INumpadConfig | undefined> = ref(undefined)
const selectClientConfig: Ref<ISelectClientConfig | undefined> = ref(undefined)
const codConfirmConfig: Ref<ICodConfirmConfig> = ref({})
const showTerminalRegister = ref(false)
const showPinModal = ref(false)
const pinModalSession = ref<TableSession | null>(null)
let pinModalTimeout: ReturnType<typeof setTimeout> | null = null

const emitter = inject('emitter') as Emitter
const qrEnabled = computed(() => TableSessionService.isQrOrderingEnabled())

function onShowSessionPin({ session, immediate }: { session: TableSession, immediate?: boolean }) {
  if (pinModalTimeout) clearTimeout(pinModalTimeout)
  const show = () => {
    pinModalSession.value = session
    showPinModal.value = true
    pinModalTimeout = null
  }
  if (immediate) {
    show()
  } else {
    pinModalTimeout = setTimeout(show, 500)
  }
}

onMounted(() => {
  if (qrEnabled.value) {
    emitter.on('showSessionPin', onShowSessionPin)
  }
})

onUnmounted(() => {
  if (qrEnabled.value) {
    emitter.off('showSessionPin', onShowSessionPin)
  }
  if (pinModalTimeout) clearTimeout(pinModalTimeout)
})

const appPages: ComputedRef<IAppPage[]> = computed(() => [{
  title: t('general.sell_items'),
  url: '/main/sales',
  icon: 'i-lucide-receipt',
  iconLine: 'i-ph-receipt',
  iconSolid: 'i-ph-receipt-fill',
  disabled: false,
  active: route.matched.some(r => r.path.includes('/main/sales')),
  badge: null,
},{
  title: t('general.working_day'),
  url: '/main/working-day',
  icon: 'i-lucide-calendar',
  iconLine: 'i-ph-calendar',
  iconSolid: 'i-ph-calendar-fill',
  disabled: false,
  active: route.matched.some(r => r.path.includes('/main/working-day')),
  iconWarning: settlementOverdue.value,
},{
  title: t('general.receipt', 2),
  url: '/main/receipts',
  icon: 'i-lucide-files',
  iconLine: 'i-ph-files',
  iconSolid: 'i-ph-files-fill',
  disabled: !UserService.allows('allow_receipts'),
  active: route.matched.some(r => r.path.includes('/main/receipts'))
},{
  title: t('general.report', 2),
  url: '/main/reports',
  icon: 'i-lucide-book-open',
  iconLine: 'i-ph-book-open',
  iconSolid: 'i-ph-book-open-fill',
  disabled: !UserService.allows('allow_cash_register'),
  active: route.matched.some(r => r.path.includes('/main/reports'))
},
...(ordersHubVisible.value ? [{
  title: t('orders_hub.title'),
  url: '/main/orders-hub',
  icon: 'i-lucide-bell',
  iconLine: 'i-ph-bell',
  iconSolid: 'i-ph-bell-fill',
  disabled: false,
  active: route.matched.some(r => r.path.includes('/main/orders-hub')),
  badge: ordersHubBadgeCount.value,
  iconWarning: ordersHubHasActive.value,
}] : []),
{
  title: t('general.options'),
  url: '/main/options',
  icon: 'i-lucide-layout-grid',
  iconLine: 'i-ph-squares-four',
  iconSolid: 'i-ph-squares-four-fill',
  disabled: false,
  active: route.matched.some(r => r.path.includes('/main/options'))
}]);

const openNumpad = (config: INumpadConfig) => {
  numpadConfig.value = config
  isNumpadOpen.value = true
}

const openSelectClient = (config: ISelectClientConfig) => {
  selectClientConfig.value = config
  isClientSelectModalOpen.value = true
}

/** See the singleton-overlay comment in the template. */
function useLateMount(openRef: Ref<boolean>) {
  const mounted = ref(false)
  const instanceKey = ref(0)
  let unmountTimer: ReturnType<typeof setTimeout> | undefined

  watch(openRef, (open) => {
    clearTimeout(unmountTimer)
    if (open) {
      // New instance per open → teleport anchor lands at the current body
      // end, above whatever is open right now.
      instanceKey.value++
      mounted.value = true
      return
    }
    // Outlive the close animation and the falling-edge cancel watcher.
    // Re-armed on every edge so a close→reopen→close never unmounts early.
    unmountTimer = setTimeout(() => {
      if (!openRef.value) mounted.value = false
    }, 400)
  }, { immediate: true })

  return { mounted, instanceKey }
}

const clientSelect = useLateMount(isClientSelectModalOpen)
const numpad = useLateMount(isNumpadOpen)
const terminalRegister = useLateMount(showTerminalRegister)
const codConfirmMount = useLateMount(isCodConfirmOpen)

const openCodConfirm = (config: ICodConfirmConfig) => {
  if (isCodConfirmOpen.value || codConfirmMount.mounted.value) {
    // Displace the open dialog through a real falling edge: its cancelHandler
    // settles exactly once, and the new config snapshots on a fresh rising
    // edge. Without this, an open-while-open call (e.g. a background showAlert
    // during a confirm) is silently dropped with cross-wired handlers.
    //
    // `mounted` too, not just the model: a confirm's button closes it in the
    // same tick its async yesHandler may still raise an alert (a blocked
    // receipt cancel). The model already reads false there, but nothing has
    // flushed yet, so setting it straight back to true coalesces into "no
    // change" — no rising edge, the alert config never snapshots, and the
    // stale confirm stays on screen. The instance is still mounted for the
    // close animation, which is exactly the signal that a falling edge is owed.
    isCodConfirmOpen.value = false
    nextTick(() => {
      codConfirmConfig.value = config
      isCodConfirmOpen.value = true
    })
    return
  }

  codConfirmConfig.value = config
  isCodConfirmOpen.value = true
}

registerAlertHost(openCodConfirm)

provide('openNumpad', openNumpad);
provide('openSelectClient', openSelectClient);
provide('codConfirm', openCodConfirm);
provide('isNumpadOpen', isNumpadOpen);
provide('showTerminalRegister', showTerminalRegister);
provide('isClientSelectModalOpen', isClientSelectModalOpen);
provide('isCodConfirmOpen', isCodConfirmOpen);
provide('registerTerminal', () => (showTerminalRegister.value = true));

function refreshApp() {
  if(!connectionService.isOnline) {
    toast(t('connection.internet_offline_message_not_allowed'), 'error');
    return
  }

  location.reload()
}

function toggleTrainingMode() {
  Store.set('trainingMode', !store.trainingMode)
}

function goToPage(page: IAppPage) {
  if (page.disabled) return
  router.push(page.url)
}

function logout() {
  UserService.logout();
  router.push({ name: 'userLogin' })
}
</script>

<style lang="scss" scoped>
// Bottom-bar tab switch: the color eases over while the line glyph fades out
// and its solid twin fades in from 80% — the theme's 160ms ease, no bounce
// (reduced motion: the fade alone).
.tab-btn {
  transition: color var(--app-transition);
}

.tab-glyph {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: opacity var(--app-transition), transform var(--app-transition);
}

.tab-glyph--solid {
  opacity: 0;
  transform: scale(0.8);
}

.tab-btn--solid {
  .tab-glyph--line {
    opacity: 0;
  }

  .tab-glyph--solid {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab-glyph--solid {
    transform: none;
  }
}

// Below 390px the six tab buttons crowd and the labels clip. Scale the
// label and icon down with viewport width so every option stays visible;
// at/above 390px the base sizes are untouched (the vw term only dips
// under the max below that width). The tabs drop their side padding so a
// label gets the tab's whole width ("Radni dan" stays on one line down to
// ~336px), and the icons sit on one top line, so a label that still has to
// wrap hangs below instead of lifting its icon above the others.
@media (max-width: 390px) {
  .tab-btn {
    padding-inline: 0;
    justify-content: flex-start;
  }

  .tab-label {
    font-size: var(--type-nav-label);
  }

  .tab-icon {
    width: clamp(17px, 5.8vw, 24px);
    height: clamp(17px, 5.8vw, 24px);
  }
}

// Training-mode banner: solid error strip across the content column, white
// content (design §2: text on solid color is always white). min-height keeps
// it a lean chrome strip; the action stays tappable inside it.
.training-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-height: 44px;
  padding: 4px 12px 4px 16px;
  background: var(--ui-error);
  color: #ffffff;
}

// Icon rail — geometry from design Sidebar.dc.html (88px rail, 74px pills,
// active 66px solid plum). The clamp/vh terms keep every item on screen on
// short viewports (a device constraint the mockup doesn't cover).
.sidebar-menu {
  width: 88px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: clamp(6px, 1.4vh, 10px) 8px;
  // The rail owns the Android edge-to-edge status-bar inset itself —
  // otherwise the system top bar covers the brand logo / first item.
  padding-top: calc(clamp(6px, 1.4vh, 10px) + var(--app-safe-area-top, 0px));
  height: 100%;
  overflow: hidden;
  color: var(--app-rail-text);
  background-color: var(--app-rail-bg);
  border-right: 1px solid var(--app-rail-border);

  .rail-item {
    position: relative;
    width: 74px;
    border-radius: var(--radius-btn);
    padding: clamp(5px, 1.25vh, 9px) 0 clamp(4px, 0.97vh, 7px);
    margin-bottom: clamp(1px, 0.28vh, 2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(2px, 0.56vh, 4px);
    font-size: var(--type-nav-label);
    font-weight: 600;
    text-align: center;
    cursor: pointer;
    // Select/unselect animates (motion tokens): the plum pill fades in and
    // the item narrows to the active width instead of snapping.
    transition:
      background-color var(--app-transition),
      color var(--app-transition),
      box-shadow var(--app-transition),
      width var(--app-transition);

    .rail-icon {
      width: clamp(16px, 2.64vh, 19px);
      height: clamp(16px, 2.64vh, 19px);
    }

    .rail-icon--sm {
      width: clamp(15px, 2.5vh, 18px);
      height: clamp(15px, 2.5vh, 18px);
    }

    .rail-label {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.2;
    }

    &.selected {
      width: 66px;
      background: var(--app-rail-active-bg);
      // White on the solid plum pill in BOTH themes (design §2: text on
      // solid color is always white) — deliberately not a semantic token.
      color: #ffffff;
      font-weight: 600;
      box-shadow: var(--app-rail-active-shadow);
    }

    &.attention {
      color: var(--app-rail-attention);
      font-weight: 600;
    }

    // Training toggle while the mode is ON: solid error pill, white content
    // (white on solid, like .selected). Amber stays the hub-attention color;
    // training carries the same red as its global banner.
    &.training {
      background: var(--ui-error);
      color: #ffffff;
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    .rail-badge {
      position: absolute;
      top: 3px;
      right: 13px;
      min-width: 14px;
      height: 14px;
      padding: 0 3px;
      border-radius: 7px;
      background: var(--app-rail-attention);
      color: var(--app-rail-badge-text);
      font-size: var(--type-nav-badge);
      font-weight: 800;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      box-shadow: 0 0 0 2px var(--app-rail-bg);
    }

    &.selected .rail-badge {
      right: 9px;
      box-shadow: 0 0 0 2px var(--app-rail-active-bg);
    }
  }

  .rail-divider {
    width: 56px;
    height: 1px;
    background: var(--app-rail-divider);
    margin: clamp(3px, 0.83vh, 6px) 0;
  }

  .additional {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .rail-status {
    margin: clamp(4px, 1.11vh, 8px) 0 clamp(3px, 0.83vh, 6px);
  }

  .sidebar-top {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .sidebar-brand {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    // Fluid spacing below the logo so it gives up room on short viewports too.
    padding-bottom: clamp(4px, 2.22vh, 16px);

    img {
      max-width: 100%;
      object-fit: contain;
    }
  }
}

// Desktop page switch (rail navigation): cross-fade with a slight rise on the
// motion tokens. Mobile gets no transition name, so switches stay instant.
.page-switch-enter-active {
  transition: opacity var(--app-transition), transform var(--app-transition);
}
.page-switch-leave-active {
  transition: opacity var(--app-transition-fast);
}
.page-switch-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.page-switch-leave-to {
  opacity: 0;
}
</style>
